import {
  groundSingleWitnessCoreferenceChains,
  projectGroundedCoreferenceChainsToSourceAnchors,
  validateSingleWitnessCoreferenceChainOutput,
  type GroundedSingleWitnessCoreferenceChainMention,
  type PreparedSingleWitnessCoreferenceChainExtractionCall,
  type SingleWitnessCoreferenceChainCharacter,
  type SingleWitnessCoreferenceChainOutput,
} from '@variorum/character-index'
import type {
  CharacterEntity,
  CharacterLayerDetectorOutput,
  CharacterOccurrence,
} from '../types/characterLayerTypes'

export const PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT = 'package-coreference-chain-v1'

export interface AdaptPackageCoreferenceChainsArgs {
  rawExtraction: unknown
  preparedCall: Pick<PreparedSingleWitnessCoreferenceChainExtractionCall, 'sourceText' | 'sourceMap'>
  chapterId: string
  createdAt?: string
}

type PreparedCoreferenceCall = Pick<PreparedSingleWitnessCoreferenceChainExtractionCall, 'sourceText' | 'sourceMap'>
type PreparedCoreferenceSourceAnchor = GroundedSingleWitnessCoreferenceChainMention['sourceAnchors'][number]
type CharacterIndexMentionType = 'name' | 'coref'

interface AnchorDisplayResolution {
  displayTextSlice: string
  displayRowText: string
  displayRowLength: number
  matchesSourceWindow: boolean
  reason?: string
}

const normalizeText = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : ''
)

const stableHash = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

const isFiniteOffset = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
)

const normalizeMentionType = (value: unknown): CharacterIndexMentionType | null => (
  value === 'name' || value === 'coref' ? value : null
)

const getSourceAnchorLookupKey = ({
  localEnd,
  localStart,
  rowId,
  witnessId,
}: {
  witnessId: string
  rowId: string
  localStart?: number | null
  localEnd?: number | null
}): string => [
  witnessId,
  rowId,
  isFiniteOffset(localStart) ? localStart : '',
  isFiniteOffset(localEnd) ? localEnd : '',
].join('::')

const buildProjectedMentionTypeByAnchorKey = (
  grounded: ReturnType<typeof groundSingleWitnessCoreferenceChains>,
): Map<string, CharacterIndexMentionType> => {
  const map = new Map<string, CharacterIndexMentionType>()
  projectGroundedCoreferenceChainsToSourceAnchors(grounded).forEach(anchor => {
    const mentionType = normalizeMentionType((anchor as typeof anchor & { mentionType?: unknown }).mentionType)
    if (!mentionType) {
      return
    }
    const key = getSourceAnchorLookupKey(anchor)
    if (!map.has(key)) {
      map.set(key, mentionType)
    }
  })
  return map
}

const resolveAnchorDisplayText = (
  preparedCall: PreparedCoreferenceCall,
  anchor: PreparedCoreferenceSourceAnchor,
): AnchorDisplayResolution => {
  const localStart = isFiniteOffset(anchor.localStart) ? anchor.localStart : null
  const localEnd = isFiniteOffset(anchor.localEnd) ? anchor.localEnd : null
  if (localStart === null || localEnd === null || localEnd <= localStart) {
    return {
      displayTextSlice: '',
      displayRowText: '',
      displayRowLength: 0,
      matchesSourceWindow: false,
      reason: 'invalid-local-offsets',
    }
  }

  const textSpan = preparedCall.sourceMap.find(span => {
    const spanLocalStart = isFiniteOffset(span.localStart) ? span.localStart : null
    const spanLocalEnd = isFiniteOffset(span.localEnd) ? span.localEnd : null
    return span.spanKind === 'text' &&
      span.witnessId === anchor.witnessId &&
      span.rowId === anchor.rowId &&
      spanLocalStart !== null &&
      spanLocalEnd !== null &&
      localStart >= spanLocalStart &&
      localEnd <= spanLocalEnd
  })

  if (!textSpan) {
    return {
      displayTextSlice: '',
      displayRowText: '',
      displayRowLength: 0,
      matchesSourceWindow: false,
      reason: 'missing-source-map-text-span',
    }
  }

  const spanLocalStart = isFiniteOffset(textSpan.localStart) ? textSpan.localStart : 0
  const displayRowText = preparedCall.sourceText.slice(textSpan.start, textSpan.end)
  const displayTextSlice = displayRowText.slice(localStart - spanLocalStart, localEnd - spanLocalStart)
  const sourceWindow = typeof anchor.window === 'string' ? anchor.window : ''

  return {
    displayTextSlice,
    displayRowText,
    displayRowLength: displayRowText.length,
    matchesSourceWindow: sourceWindow.length === 0 || displayTextSlice === sourceWindow,
    ...(sourceWindow.length > 0 && displayTextSlice !== sourceWindow ? { reason: 'display-source-window-mismatch' } : {}),
  }
}

const getAnchorName = (character: SingleWitnessCoreferenceChainCharacter): string => (
  normalizeText(character.anchor?.name)
)

const getAnchorTransliteration = (character: SingleWitnessCoreferenceChainCharacter): string => (
  normalizeText(character.anchor?.transliteration)
)

const buildCharacterId = ({
  chapterId,
  character,
  chainIndex,
}: {
  chapterId: string
  character: SingleWitnessCoreferenceChainCharacter
  chainIndex: number
}): string => (
  `character-coref-${stableHash([
    chapterId,
    chainIndex,
    getAnchorName(character),
    getAnchorTransliteration(character),
    character.chain[0]?.context || '',
  ].join(':'))}`
)

const buildOccurrenceId = ({
  chapterId,
  characterId,
  mention,
  rowId,
  witnessId,
  localStart,
  localEnd,
}: {
  chapterId: string
  characterId: string
  mention: GroundedSingleWitnessCoreferenceChainMention
  rowId: string
  witnessId: string
  localStart?: number
  localEnd?: number
}): string => (
  `character-occ-coref-${stableHash([
    chapterId,
    characterId,
    witnessId,
    rowId,
    localStart ?? '',
    localEnd ?? '',
    mention.mention,
    mention.context,
  ].join(':'))}`
)

const isValidCoreferenceOutput = (
  rawExtraction: unknown,
): rawExtraction is SingleWitnessCoreferenceChainOutput => (
  validateSingleWitnessCoreferenceChainOutput(rawExtraction).length === 0
)

const buildDisplayNameCounts = (
  characters: readonly SingleWitnessCoreferenceChainCharacter[],
): Map<string, number> => {
  const counts = new Map<string, number>()
  characters.forEach(character => {
    const name = getAnchorName(character)
    if (!name) {
      return
    }
    const key = name.toLowerCase()
    counts.set(key, (counts.get(key) || 0) + 1)
  })
  return counts
}

const buildDisplayName = ({
  character,
  chainOrdinal,
  duplicateCounts,
}: {
  character: SingleWitnessCoreferenceChainCharacter
  chainOrdinal: number
  duplicateCounts: Map<string, number>
}): string => {
  const anchorName = getAnchorName(character) || `[chain ${chainOrdinal}]`
  const duplicateCount = duplicateCounts.get(anchorName.toLowerCase()) || 0
  return duplicateCount > 1 ? `${anchorName} [chain ${chainOrdinal}]` : anchorName
}

export const adaptPackageCoreferenceChainsToDetectorOutput = ({
  rawExtraction,
  preparedCall,
  chapterId,
  createdAt,
}: AdaptPackageCoreferenceChainsArgs): CharacterLayerDetectorOutput => {
  const timestamp = createdAt || new Date().toISOString()
  const validationIssues = validateSingleWitnessCoreferenceChainOutput(rawExtraction)
  if (!isValidCoreferenceOutput(rawExtraction)) {
    return {
      characters: [],
      occurrences: [],
      notes: [],
      metadata: {
        output_contract: PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT,
        chain_count: 0,
        grounded_count: 0,
        ambiguous_count: 0,
        not_found_count: 0,
        validation_issues: validationIssues,
      },
    }
  }

  const grounded = groundSingleWitnessCoreferenceChains(preparedCall, rawExtraction)
  const projectedMentionTypeByAnchorKey = buildProjectedMentionTypeByAnchorKey(grounded)
  const duplicateCounts = buildDisplayNameCounts(rawExtraction.characters)
  const characters: CharacterEntity[] = []
  const occurrences: CharacterOccurrence[] = []
  const skippedSourceAnchors: Array<Record<string, unknown>> = []

  grounded.characters.forEach((chain, chainIndex) => {
    const groundedMentions = chain.chain.filter(mention => mention.status === 'grounded')
    if (groundedMentions.length === 0) {
      return
    }

    const sourceCharacter = rawExtraction.characters[chainIndex]
    const chainOrdinal = chainIndex + 1
    const characterId = buildCharacterId({
      chapterId,
      character: sourceCharacter,
      chainIndex,
    })
    const displayName = buildDisplayName({
      character: sourceCharacter,
      chainOrdinal,
      duplicateCounts,
    })
    const anchorTransliteration = getAnchorTransliteration(sourceCharacter)

    characters.push({
      character_id: characterId,
      display_name: displayName,
      aliases: [],
      status: 'auto',
      origin: 'llm',
      metadata: {
        output_contract: PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT,
        chain_index: chainIndex,
        chain_anchor: sourceCharacter.anchor.name,
        ...(anchorTransliteration ? { transliteration: anchorTransliteration } : {}),
        coreference_chain_size: sourceCharacter.chain.length,
      },
      created_at: timestamp,
      updated_at: timestamp,
    })

    groundedMentions.forEach(mention => {
      mention.sourceAnchors.forEach(anchor => {
        const localStart = typeof anchor.localStart === 'number' ? anchor.localStart : undefined
        const localEnd = typeof anchor.localEnd === 'number' ? anchor.localEnd : undefined
        const mentionType = projectedMentionTypeByAnchorKey.get(getSourceAnchorLookupKey({
          witnessId: anchor.witnessId,
          rowId: anchor.rowId,
          localStart,
          localEnd,
        })) || 'coref'
        const displayResolution = resolveAnchorDisplayText(preparedCall, anchor)
        if (!displayResolution.matchesSourceWindow) {
          skippedSourceAnchors.push({
            reason: displayResolution.reason || 'unresolved-display-text',
            chain_index: chainIndex,
            chain_anchor: sourceCharacter.anchor.name,
            mention: mention.mention,
            context: mention.context,
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            ...(typeof localStart === 'number' ? { localStart } : {}),
            ...(typeof localEnd === 'number' ? { localEnd } : {}),
            ...(typeof anchor.window === 'string' ? { source_window: anchor.window } : {}),
            ...(displayResolution.displayTextSlice ? { display_text_slice: displayResolution.displayTextSlice } : {}),
          })
          return
        }
        occurrences.push({
          occurrence_id: buildOccurrenceId({
            chapterId,
            characterId,
            mention,
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            localStart,
            localEnd,
          }),
          character_id: characterId,
          locator: {
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            ...(typeof localStart === 'number' ? { localStart } : {}),
            ...(typeof localEnd === 'number' ? { localEnd } : {}),
          },
          role_labels: ['mentioned'],
          label: mention.mention,
          summary: mention.context,
          status: 'auto',
          origin: 'llm',
          metadata: {
            output_contract: PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT,
            grounding_status: mention.status,
            chain_index: chainIndex,
            chain_anchor: sourceCharacter.anchor.name,
            mention_type: mentionType,
            mention: mention.mention,
            context: mention.context,
            ...(mention.host ? { host: mention.host } : {}),
            ...(mention.matchKind ? { match_kind: mention.matchKind } : {}),
            ...(mention.matchedText ? { matched_text: mention.matchedText } : {}),
            ...(anchor.window ? { anchor_window: anchor.window } : {}),
            display_text_slice: displayResolution.displayTextSlice,
            display_text_match: true,
            display_row_length: displayResolution.displayRowLength,
            ...(anchor.unitUuid ? { unitUuid: anchor.unitUuid } : {}),
            ...(anchor.unitLabel ? { unitLabel: anchor.unitLabel } : {}),
            ...(anchor.unitId ? { unitId: anchor.unitId } : {}),
          },
          created_at: timestamp,
          updated_at: timestamp,
        })
      })
    })
  })

  const allMentions = grounded.characters.flatMap(character => character.chain)
  return {
    characters,
    occurrences,
    notes: [],
    metadata: {
      output_contract: PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT,
      chain_count: rawExtraction.characters.length,
      grounded_count: allMentions.filter(mention => mention.status === 'grounded').length,
      ambiguous_count: allMentions.filter(mention => mention.status === 'ambiguous').length,
      not_found_count: allMentions.filter(mention => mention.status === 'not_found').length,
      skipped_source_anchor_count: skippedSourceAnchors.length,
      skipped_source_anchors: skippedSourceAnchors,
      grounding: grounded,
      validation_issues: [],
      raw_shape: asRecord(rawExtraction) ? 'object' : typeof rawExtraction,
    },
  }
}
